import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import { invoicesApi } from '../../../lib/api';
import { useApi } from '../../../hooks/useApi';
import { useAppStore } from '../../../lib/store';
import { formatDate } from '../../../lib/dateUtils';

const statusColors = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  overdue: 'error',
  cancelled: 'default',
} as const;

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['finance', 'common']);
  const user = useAppStore((state) => state.user);
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  const { data: invoice, loading, error } = useApi(
    async () => {
      if (!id) throw new Error('No invoice ID provided');
      const response = await invoicesApi.getById(id);
      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    [id]
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </ResponsiveContainer>
    );
  }

  if (error || !invoice) {
    return (
      <ResponsiveContainer>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || t('common:somethingWentWrong')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/finance/invoices')}
        >
          {t('common:back')}
        </Button>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/finance/invoices')}
          variant="outlined"
        >
          {t('common:back')}
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {invoice.invoice_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('vatInvoice', 'VAT Invoice')}
          </Typography>
        </Box>
        <Chip
          label={t(`invoiceStatuses.${invoice.status}`, invoice.status)}
          color={statusColors[invoice.status as keyof typeof statusColors] || 'default'}
          className="no-print"
        />
        <Button
          startIcon={<PrintIcon />}
          variant="outlined"
          onClick={() => window.print()}
          className="no-print"
        >
          {t('print', 'Print')}
        </Button>
        {isManagerOrAdmin && (
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() => navigate(`/dashboard/finance/invoices/${id}/edit`)}
            className="no-print"
          >
            {t('common:edit')}
          </Button>
        )}
      </Box>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .printable-area, .printable-area * {
              visibility: visible;
            }
            .printable-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            .MuiCard-root {
              box-shadow: none !important;
              border: 1px solid #ddd !important;
            }
          }
          .print-only {
            display: none;
          }
        `}
      </style>

      {/* Print Header - Only visible when printing */}
      <Box className="print-only" sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid #1976d2' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {t('companyName', 'Makeen Property Management')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('vatInvoice', 'VAT Invoice')}
        </Typography>
      </Box>

      <Box className="printable-area">
        <Grid container spacing={3}>
          {/* Main Invoice Card */}
          <Grid item xs={12} md={8}>
            <Card>
            <CardContent>
              {/* Invoice Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ReceiptIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {t('vatInvoice', 'VAT Invoice')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('ksaVatCompliant', 'KSA VAT Compliant')}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {invoice.invoice_number}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Dates */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('issueDate', 'Issue Date')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(invoice.issue_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('dueDate', 'Due Date')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(invoice.due_date)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Description */}
              {invoice.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('description', 'Description')}
                  </Typography>
                  <Typography variant="body1">
                    {invoice.description}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Amount Breakdown */}
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>{t('description', 'Description')}</TableCell>
                      <TableCell align="right">{t('amount', 'Amount')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('subtotal', 'Subtotal')}</TableCell>
                      <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t('vat', 'VAT')} (15%)</TableCell>
                      <TableCell align="right">{formatCurrency(invoice.vat_amount)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('totalAmount', 'Total Amount')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {formatCurrency(invoice.total_amount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Customer Info */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('customerInformation', 'Customer Information')}
              </Typography>

              {/* Tenant */}
              {invoice.tenant && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('tenant', 'Tenant')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {invoice.tenant.first_name} {invoice.tenant.last_name}
                  </Typography>
                  {invoice.tenant.email && (
                    <Typography variant="body2" color="text.secondary">
                      {invoice.tenant.email}
                    </Typography>
                  )}
                  {invoice.tenant.phone_number && (
                    <Typography variant="body2" color="text.secondary">
                      {invoice.tenant.phone_number}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Property */}
              {invoice.property && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <HomeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('property', 'Property')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {invoice.property.title}
                  </Typography>
                  {invoice.property.address && (
                    <Typography variant="body2" color="text.secondary">
                      {invoice.property.address}
                      {invoice.property.city && `, ${invoice.property.city}`}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('timeline', 'Timeline')}
              </Typography>

              {/* Created Date */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t('createdAt', 'Created')}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {formatDate(invoice.created_at)}
                </Typography>
              </Box>

              {/* Updated Date */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t('lastUpdated', 'Last Updated')}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {formatDate(invoice.updated_at)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Print Signature Section - Only visible when printing */}
        <Grid item xs={12} className="print-only">
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #ddd' }}>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #333', pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('signatureLine', 'Signature')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #333', pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('dateLine', 'Date')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {t('authorizedSignatory', 'Authorized Signatory')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      </Box>
    </ResponsiveContainer>
  );
}
